/**
 * Client for the camera-calibration API, and the one rule that belongs on this side of it.
 *
 * See backend/camera_calibration.py and
 * docs/superpowers/specs/2026-08-20-camera-geolocation-design.md
 */

export type NormPoint = [number, number];
export type Bbox = [number, number, number, number];

export interface DetectionBox {
  bbox: Bbox;
  conf: number;
  class: string;
}

export interface Correspondence {
  u: number;
  v: number;
  lat: number;
  lng: number;
}

export interface Calibration {
  id: number;
  stream_key: string;
  name: string;
  correspondences: Correspondence[];
  rms_error_m: number | null;
  updated_at?: string;
}

export interface SaveResult {
  calibration: Calibration;
  residuals_m: number[];
  outliers: number[];
}

export type GeoFix =
  | {
      ok: true;
      u: number; v: number; label?: string | null;
      lat: number; lng: number;
      accuracy_m: number;
      method: string;
      warnings: string[];
    }
  | { ok: false; u: number; v: number; label?: string | null; reason: string };

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

/**
 * The pixel that gets geolocated: the bottom edge's centre, normalised.
 *
 * A homography maps the GROUND PLANE and nothing else, so the pixel handed to it has to be
 * where the object touches the ground. This deliberately differs from
 * detection_roi.box_anchor(), which uses the centroid for anything that is not a person —
 * correct there, because "is this fire inside my area" is about where the fire's middle is.
 * Here a centroid sits a metre or more in the air and projects long. For a fire or a vehicle
 * the bottom edge is still the best ground estimate available; the accuracy circle carries
 * the rest.
 */
export function groundAnchor(bbox: Bbox, frameW: number, frameH: number): NormPoint {
  const [x1, y1, x2, y2] = bbox;
  if (!frameW || !frameH) return [0, 0];
  return [clamp01((x1 + x2) / 2 / frameW), clamp01(Math.max(y1, y2) / frameH)];
}

/**
 * The box under a normalised click, smallest first.
 *
 * Smallest wins so that a person standing in front of a large "crowd" box resolves to the
 * person — the operator is pointing at the thing they can see, which is the nearer one.
 */
export function boxAtPoint(
  boxes: DetectionBox[], u: number, v: number, frameW: number, frameH: number,
): DetectionBox | null {
  if (!frameW || !frameH) return null;
  const x = u * frameW;
  const y = v * frameH;
  const hits = boxes.filter(b => {
    const [x1, y1, x2, y2] = b.bbox;
    return x >= Math.min(x1, x2) && x <= Math.max(x1, x2)
      && y >= Math.min(y1, y2) && y <= Math.max(y1, y2);
  });
  if (!hits.length) return null;
  const area = (b: DetectionBox) =>
    Math.abs(b.bbox[2] - b.bbox[0]) * Math.abs(b.bbox[3] - b.bbox[1]);
  return hits.reduce((best, b) => (area(b) < area(best) ? b : best));
}

type Headers = (json?: boolean) => HeadersInit;

async function detail(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => null);
  return (body && body.detail) || fallback;
}

export async function fetchCalibration(
  apiUrl: string, authHeaders: Headers,
): Promise<Calibration | null> {
  const res = await fetch(`${apiUrl}/api/camera-calibration`, { headers: authHeaders() });
  if (!res.ok) return null;
  const data = await res.json();
  return data?.calibration ?? null;
}

export async function saveCalibration(
  apiUrl: string, authHeaders: Headers, correspondences: Correspondence[],
): Promise<SaveResult> {
  const res = await fetch(`${apiUrl}/api/camera-calibration`, {
    method: 'PUT',
    headers: authHeaders(true),
    body: JSON.stringify({ correspondences, name: 'Camera calibration' }),
  });
  if (!res.ok) throw new Error(await detail(res, `Save failed (${res.status})`));
  return res.json();
}

export async function clearCalibration(apiUrl: string, authHeaders: Headers): Promise<void> {
  const res = await fetch(`${apiUrl}/api/camera-calibration`, {
    method: 'DELETE', headers: authHeaders(),
  });
  if (!res.ok && res.status !== 404) {
    throw new Error(await detail(res, `Clear failed (${res.status})`));
  }
}

export async function projectPoints(
  apiUrl: string, authHeaders: Headers, points: { u: number; v: number; label?: string }[],
): Promise<GeoFix[]> {
  const res = await fetch(`${apiUrl}/api/camera-calibration/project`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ points }),
  });
  if (!res.ok) throw new Error(await detail(res, `Could not geolocate (${res.status})`));
  const data = await res.json();
  return data.results as GeoFix[];
}
